import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { uniqueId } from 'lodash'
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { RootState } from '../../../../states/containers/store'
import {
  createTaskLabelMasterImport,
  createTaskLabelMasterSetting,
  fetchTaskLabelMasterSetting,
  updateTaskLabelMasterSetting,
} from '../../../../states/containers/task/taskSlice'
import { RequestGetTaskLabelMasterSetting, RequestPatchTaskLabelMasterSetting } from '../../../../states/types/task'
import { format } from '../../../../utils/date'
import LoadingSquare from '../../../components/common/LoadingSquare'
import Tips from '../../../components/common/Tips'
import AddLabelMasterModal from '../../../components/task/AddLabelMasterModal'
import ConfirmationModal from '../../../components/task/ConfirmationModal'
import { useAlert } from 'react-alert'
import GroupButton from '../../../components/common/GroupButton'
import ImportLabelMasterModal from '../../../components/task/ImportLabelMasterModal'
import { getGetPrams, setPath } from '../../../../utils/url'
import { URL } from '../../../constants/url'
import SearchConditionModule from '../../../components/task/SearchConditionModule'
import { COLOR_LIST } from '../../../constants/taskColor'
import { LangValue } from '../../../../states/types/common'
import SearchConditionModuleForHighVolume from '../../../components/task/SearchConditionModuleForHighVolume'

interface LabelMasters {
  id: number
  name: string
  color: string
  created_at: string
  created_by: string | null
  label_group_ids: number[]
  label_group_count: number
  description?: string | null
}

interface ModalState {
  id?: number
  name?: string
  color?: string
  labelGroupIds?: number[]
  description?: string | null
  type: string
}

interface ConfirmationModalState {
  display: boolean
  type: string
  name: string
  callback: VoidFunction
}

interface GetParams {
  colors?: string
  master_relation_label_group_ids?: string
  only_unassigned?: string
  label_master_keywords?: string
}

const Index: FC = () => {
  const {
    task,
    login,
  } = useSelector((state: RootState) => state)

  const { language } = useParams<{ language: LangValue }>()
  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const history = useHistory()

  const DISPLAY_LIMIT = 30
  const [pageOffset, setPageOffset] = useState<number>(1)
  const [filterColors, setFilterColors] = useState<string[]>([])
  const [filterLabelGroupIds, setFilterLabelGroupIds] = useState<number[]>([])
  const [filterOnlyUnassigned, setFilterOnlyUnassigned] = useState<boolean>(false)
  const [filterKeywords, setFilterKeywords] = useState<string[]>([])

  const [data, setData] = useState<LabelMasters[]>([])

  const [isDisplayAddTask, setIsDisplayAddTask] = useState<boolean>(false)

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    name: "",
    callback: () => {
      // none
    },
  })

  const modalStateInit = {
    type: "create",
  }

  const [modalState, setModalState] = useState<ModalState>(modalStateInit)

  const labelGroupList = useMemo(() => (task.taskLabelMasterSetting?.label_groups || []), [task])

  const [isOpenImportModal, setIsOpenImportModal] = useState<boolean>(false)

  const [fetchTrigger, setFetchTrigger] = useState<string>("")
  const [pollingTrigger, setPollingTrigger] = useState<string>("")
  const [loadTrigger, setLoadTrigger] = useState<boolean>(false)

  const [isLoadingFirst, setIsLoadingFirst] = useState<boolean>(true)
  const [isLoadingList, setIsLoadingList] = useState<boolean>(false)
  const [isStop, setIsStop] = useState<boolean>(false)
  const stopRef = useRef(isStop)

  const [isDataLoad, setIsDataLoad] = useState<boolean>(false)

  const currentScroll = () => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
  const bottomHeight = () => document.getElementsByClassName("main")[0].clientHeight
  const windowHeight = () => document.documentElement.clientHeight

  const onScroll = useCallback(() => {
    const adjustment = window.innerWidth >= 1280 ? 0.90 : 0.85
    if (!stopRef.current && !isLoadingList && (bottomHeight() * adjustment) < (currentScroll() + windowHeight())) {
      setIsDataLoad(true)
    }
  }, [stopRef])

  const refresh = (): void => {
    setIsLoadingFirst(true)
    setData([])
    setIsStop(false)
    setPageOffset(1)
    const params: RequestGetTaskLabelMasterSetting = {
      page: 1,
      limit: DISPLAY_LIMIT,
    }
    if (filterKeywords.length) {
      params.keywords = filterKeywords
    }
    if (filterLabelGroupIds.length) {
      params.label_group_ids = filterLabelGroupIds
    }
    if (filterColors.length) {
      params.colors = COLOR_LIST.map((v) => v.values).flat().filter((v) => filterColors.includes(v.label)).map((v) => v.color)
    }
    if (filterOnlyUnassigned) {
      params.only_unassigned = filterOnlyUnassigned
    }
    dispatch(fetchTaskLabelMasterSetting(
      params,
      (result) => {
        setIsLoadingFirst(false)
        setIsDataLoad(false)
        setIsLoadingList(false)
        if (!result.label_masters.length) {
          setIsStop(true)
        }
      },
    ))
  }

  const addLabelMaster = (name: string, color: string, labelGroupIds: number[], description: string | null) => {
    dispatch(createTaskLabelMasterSetting(
      {
        name: name,
        color: color,
        label_group_ids: labelGroupIds,
        description: description,
      },
      () => {
        alert.success(`${name}を新規作成しました`)
        refresh()
      },
    ))
    setIsDisplayAddTask(false)
  }

  const modLabelMaster = (id: number, name: string, color: string, labelGroupIds: number[], description: string | null) => {
    const labelMaster = data.find((v) => v.id === id)
    const tmpValue: RequestPatchTaskLabelMasterSetting = { label_master_id: id }
    if (labelMaster?.name !== name) {
      tmpValue.name = name
    }
    if (labelMaster?.color !== color) {
      tmpValue.color = color
    }
    if (labelMaster?.description !== description) {
      tmpValue.description = description
    }
    if (!login.permissions?.high_volume) {
      tmpValue.label_group_ids = labelGroupIds
    }

    dispatch(updateTaskLabelMasterSetting(
      tmpValue,
      () => {
        alert.success(`${name}の情報を変更しました`)
        refresh()
      },
    ))
    setIsDisplayAddTask(false)
  }

  const openLabelMasterModal = (type: string, id?: number, name?: string, color?: string, labelGroupIds?: number[], description?: string | null) => {
    if (type === "create") {
      setModalState(
        {
          type: "create",
          color: "#eb3333",
        },
      )
    } else {
      setModalState(
        {
          type: "update",
          id: id,
          name: name,
          color: color,
          labelGroupIds: labelGroupIds,
          description: description,
        },
      )
    }
    setIsDisplayAddTask(true)
  }

  // confirmation modal
  const openConfirmationModal = (type: string, id: number) => {
    let callback = () => {
      // none
    }
    switch (type) {
      case "label_master_delete":
        callback = () => { dispatch(updateTaskLabelMasterSetting({ label_master_id: id, disabled: true })) }
        break
      default:
        break
    }
    const name = task.taskLabelMasterSetting?.label_masters.find((v) => v.id === id)?.name
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      type: type,
      name: name || "",
      callback: callback,
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      type: "",
      name: "",
      callback: () => {
        // none
      },
    })
  }

  const addImport = (importParams: { csv_file: string, register: boolean }, modalCallback: VoidFunction) => {
    dispatch(createTaskLabelMasterImport(
      importParams,
      () => {
        if (importParams.register) {
          alert.success("業務一括登録が完了しました")
          modalCallback()
          refresh()
        }
      },
    ))
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [])

  useEffect(() => {
    const getParams = getGetPrams(location.search) as GetParams
    if (getParams.colors) {
      const selectedLabels = getParams.colors.split(',')
      setFilterColors(selectedLabels)
    }
    if (getParams.master_relation_label_group_ids) {
      const labelGroupIds = getParams.master_relation_label_group_ids.split(',').map(Number)
      setFilterLabelGroupIds(labelGroupIds)
    }
    if (getParams.label_master_keywords) {
      const keywords = decodeURIComponent(getParams.label_master_keywords).split(',')
      setFilterKeywords(keywords)
    }
    if (getParams.only_unassigned) {
      const only_unassigned = JSON.parse(getParams.only_unassigned.toLowerCase()) as boolean
      setFilterOnlyUnassigned(only_unassigned)
    }
    setLoadTrigger(true)
  }, [])

  useEffect(() => {
    if (!loadTrigger) {
      setIsLoadingFirst(false)
      setIsLoadingList(false)
      return
    }
    setIsLoadingFirst(true)
    setData([])
    setIsStop(false)
    setPageOffset(1)
    const query = []
    const params: RequestGetTaskLabelMasterSetting = {
      page: 1,
      limit: DISPLAY_LIMIT,
    }

    if (filterKeywords.length) {
      params.keywords = filterKeywords
      query.push(`label_master_keywords=${filterKeywords}`)
    }

    if (filterLabelGroupIds.length) {
      params.label_group_ids = filterLabelGroupIds
      query.push(`master_relation_label_group_ids=${filterLabelGroupIds.join(',')}`)
    }

    if (filterColors.length) {
      params.colors = COLOR_LIST.map((v) => v.values).flat().filter((v) => filterColors.includes(v.label)).map((v) => v.color)
      query.push(`colors=${filterColors?.join(',')}`)
    }

    if (filterOnlyUnassigned) {
      params.only_unassigned = filterOnlyUnassigned
      query.push(`only_unassigned=${filterOnlyUnassigned}`)
    }

    dispatch(fetchTaskLabelMasterSetting(
      params,
      (result) => {
        setIsLoadingFirst(false)
        setIsDataLoad(false)
        if (!result.label_masters.length) {
          setIsStop(true)
        }
      },
    ))
    history.push(`${setPath(URL.TASK_MASTER.path, language)}?${query.join('&')}`)
  }, [filterColors, filterLabelGroupIds, filterKeywords, filterOnlyUnassigned, loadTrigger, fetchTrigger])

  useEffect(() => {
    if (!loadTrigger || pageOffset === 1) {
      setIsDataLoad(false)
      setIsLoadingList(false)
      return
    }

    const params: RequestGetTaskLabelMasterSetting = {
      page: pageOffset,
      limit: DISPLAY_LIMIT,
    }
    if (filterKeywords.length) {
      params.keywords = filterKeywords
    }
    if (filterLabelGroupIds.length) {
      params.label_group_ids = filterLabelGroupIds
    }
    if (filterColors.length) {
      params.colors = COLOR_LIST.map((v) => v.values).flat().filter((v) => filterColors.includes(v.label)).map((v) => v.color)
    }
    if (filterOnlyUnassigned) {
      params.only_unassigned = filterOnlyUnassigned
    }

    dispatch(fetchTaskLabelMasterSetting(
      params,
      (result) => {
        setIsDataLoad(false)
        setIsLoadingList(false)
        if (!result.label_masters.length) {
          setIsStop(true)
        }
      },
    ))
  }, [pageOffset])

  useEffect(() => {
    if (!pollingTrigger) return
    const polling = setTimeout(() => {
      setFetchTrigger(uniqueId)
    }, 10 * 1000)
    // eslint-disable-next-line consistent-return
    return () => {
      if (pollingTrigger) {
        clearTimeout(polling)
      }
    }
  }, [pollingTrigger])

  useEffect(() => {
    setPollingTrigger(!task.taskLabelMasterSetting?.importing_project ? "" : uniqueId())
  }, [task.taskLabelMasterSetting])

  useEffect(() => {
    if (isLoadingFirst || isStop || !isDataLoad) {
      setIsDataLoad(false)
      return
    }
    setPageOffset(pageOffset + 1)
    setIsLoadingList(true)
  }, [isDataLoad])

  useEffect(() => {
    const arrivals = task.taskLabelMasterSetting?.label_masters ?? []
    if (!arrivals.length) return
    if (pageOffset === 1) {
      setData([...arrivals])
    } else {
      setData([...data, ...arrivals])
    }
  }, [task.taskLabelMasterSetting])

  useEffect(() => {
    stopRef.current = isStop
  }, [isStop])

  return (
    <section className="task_page">
      <Helmet>
        <title>Qasee - 工数管理 - 設定 - 業務設定</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        設定
        &nbsp;&gt;&nbsp;
        業務設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          業務設定
        </h1>
        <div className="tools">
          {
            !pollingTrigger && (
              <div className="button_area" style={{ position: "relative" }}>
                <GroupButton
                  list={[
                    {
                      icon: "plus",
                      text: "新しい業務を作成する",
                      callback: () => openLabelMasterModal("create"),
                    },
                    {
                      icon: "plus",
                      text: "新しい業務を一括登録する",
                      callback: () => setIsOpenImportModal(true),
                    },
                  ]}
                />
              </div>
            )
          }
        </div>
      </section>
      <section className="page_description">
        <p>
          プロジェクトに設定できる業務を変更できます。
        </p>
        <p>
          各プロジェクトに設定できる業務はリストに存在するものから選択できます。
        </p>
      </section>
      {
        login.permissions?.high_volume ? (
          <SearchConditionModuleForHighVolume
            name=""
            labelMasterKeywords={filterKeywords}
            callbackLabelMasterKeywords={setFilterKeywords}
            colors={filterColors}
            callbackColors={setFilterColors}
            masterRelationLabelGroup={{
              callbackIds: setFilterLabelGroupIds,
              list: task.taskLabelMasterSetting?.label_groups.map((v) => ({ id: v.label_group_id, name: v.label_group_name })) || [],
              disableState: filterOnlyUnassigned,
              callbackDisable: setFilterOnlyUnassigned,
            }}
          />
        ) : (
          <SearchConditionModule
            name=""
            colors={filterColors}
            callbackColorLabels={setFilterColors}
            masterRelationLabelGroupIds={filterLabelGroupIds}
            callbackMasterRelationLabelGroupIds={setFilterLabelGroupIds}
            masterRelationLabelGroupList={task.taskLabelMasterSetting?.label_groups.map((v) => ({ id: v.label_group_id, name: v.label_group_name }))}
            masterRelationLabelGroupDisabled={filterOnlyUnassigned}
            callbackMasterRelationLabelGroupDisabled={setFilterOnlyUnassigned}
            labelMasterKeywords={filterKeywords}
            callbackLabelMasterKeywords={setFilterKeywords}
          />
        )
      }
      {
        pollingTrigger ? (
          <>
            <LoadingSquare>
              <>
                プロジェクトインポート処理が実行中です。しばらくおまちください。
              </>
            </LoadingSquare>
          </>
        ) : (
          <>
            <section className="page_contents content-area">
              <div className="content-block">
                <div className="block-detail">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "right",
                      gap: "4px",
                      marginBottom: "8px",
                    }}
                  >
                    検索条件に該当した業務件数
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#008800",
                      }}
                    >
                      {task.taskLabelMasterSetting?.label_master_count || 0}
                    </div>
                    件
                  </div>
                  {isLoadingFirst && (
                    <LoadingSquare>
                      <>
                        業務リストを読み込んでいます…
                      </>
                    </LoadingSquare>
                  )}
                  {data.length > 0 && (
                    <>
                      <div className="master_table">
                        <div className="row_head">
                          <div>
                            編集
                          </div>
                          <div>
                            業務
                          </div>
                          <div>
                            設定プロジェクト
                          </div>
                          <div>
                            設定日時
                          </div>
                          <div>
                            作成者
                          </div>
                        </div>
                        {
                          data.map((value) => (
                            <div
                              className="row"
                              key={`value_${value.id}_${value.name}_${value.description}`}
                            >
                              <div>
                                <Tips
                                  description="編集する"
                                  className=""
                                  icon={false}
                                >
                                  <>
                                    <FontAwesomeIcon
                                      icon={['fas', 'tools']}
                                      style={{ cursor: "pointer" }}
                                      // eslint-disable-next-line max-len
                                      onClick={() => openLabelMasterModal("update", value.id, value.name, value.color, value.label_group_ids, value.description)}
                                    />
                                  </>
                                </Tips>
                                <Tips
                                  description="削除する"
                                  className=""
                                  icon={false}
                                >
                                  <>
                                    <FontAwesomeIcon
                                      icon={['fas', 'minus-circle']}
                                      style={{ cursor: "pointer" }}
                                      onClick={() => openConfirmationModal("label_master_delete", value.id)}
                                    />
                                  </>
                                </Tips>
                              </div>
                              <div>
                                <div
                                  className="color"
                                  style={{
                                    backgroundColor: value.color,
                                  }}
                                />
                                {value.name}
                                {
                                  value.description ? (
                                    <Tips
                                      description={value.description.replace(/\n/g, "%br%")}
                                      className=""
                                      icon={false}
                                    >
                                      <>
                                        <FontAwesomeIcon
                                          icon={['far', 'comment-dots']}
                                        />
                                      </>
                                    </Tips>
                                  ) : (
                                    <></>
                                  )
                                }
                              </div>
                              <div>
                                {`${value.label_group_count || 0}件`}
                              </div>
                              <div>
                                {format(value.created_at, "YYYY年MM月DD日 HH:mm:ss")}
                              </div>
                              <div>
                                {value.created_by || "-"}
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    </>
                  )}
                  {(data.length === 0 && !isLoadingFirst) && (
                    <div className="summary_no_data">
                      データがありません
                    </div>
                  )}
                  {isLoadingList && (
                    <div className="block-detail">
                      <LoadingSquare>
                        <>
                          仕分け状況データを読み込み中…
                        </>
                      </LoadingSquare>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </>
        )
      }
      <AddLabelMasterModal
        modalState={modalState}
        closeCallback={() => setIsDisplayAddTask(false)}
        addCallback={(name, color, labelGroupIds, description) => addLabelMaster(name, color, labelGroupIds, description)}
        modCallback={(id, name, color, labelGroupIds, description) => modLabelMaster(id, name, color, labelGroupIds, description)}
        isDisplay={isDisplayAddTask}
        currentList={task.taskLabelMasterSetting?.label_masters.map((v) => v.name) || []}
        labelGroupList={labelGroupList}
      />
      <ImportLabelMasterModal
        isDisplay={isOpenImportModal}
        dataList={task.taskLabelMasterImport}
        isLoading={task.isLoadingForModal || false}
        closeCallback={() => setIsOpenImportModal(false)}
        addCallback={(importParams, modalCallback) => addImport(importParams, modalCallback)}
      />
      {/* 確認モーダル */}
      <ConfirmationModal
        confirmationModalState={confirmationModalState}
        closeCallback={closeConfirmationModal}
      />
    </section>
  )
}

export default Index
